import { useEffect, useMemo, useRef, useState } from "react";

import { fetchContacts } from "@/api/crm";
import type { CrmContact } from "@/types/crm";

type CrmSearchSelectProps = {
  label: string;
  placeholder?: string;
  selectedId?: number | null;
  selectedLabel?: string | null;
  onSelect: (selection: { id: number; label: string } | null) => void;
  contactType?: "Venue" | "Person" | "Organization" | "Media" | "Other";
  helperText?: string;
};

const MIN_QUERY_LENGTH = 2;

export default function CrmSearchSelect({
  label,
  placeholder = "Search CRM…",
  selectedId = null,
  selectedLabel = "",
  onSelect,
  contactType,
  helperText,
}: CrmSearchSelectProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<CrmContact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const blurTimeoutRef = useRef<number>();

  const inputValue = open ? query : selectedLabel || "";

  useEffect(() => {
    if (!open) return undefined;
    if (query.trim().length < MIN_QUERY_LENGTH) {
      setResults([]);
      setError(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void fetchContacts({ query: query.trim(), limit: 20 })
      .then((payload) => {
        if (cancelled) return;
        let contacts = payload.contacts ?? [];
        if (contactType) {
          const normalized = contactType.toLowerCase();
          contacts = contacts.filter(
            (contact) => (contact.contact_type ?? "").toLowerCase() === normalized,
          );
        }
        setResults(contacts);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Unable to search contacts.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [contactType, open, query]);

  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        window.clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  const dropdownVisible = open && (loading || results.length > 0 || error);

  const emptyMessage = useMemo(() => {
    if (loading) return "Searching…";
    if (error) return error;
    if (query.trim().length < MIN_QUERY_LENGTH) {
      return `Type at least ${MIN_QUERY_LENGTH} characters to search.`;
    }
    if (results.length === 0) {
      return "No CRM contacts match the query.";
    }
    return null;
  }, [error, loading, query, results.length]);

  const handleSelect = (contact: CrmContact) => {
    const labelValue = contact.company_name || contact.name || `Contact #${contact.id}`;
    onSelect({ id: contact.id, label: labelValue });
    setQuery(labelValue);
    setOpen(false);
  };

  const handleClear = () => {
    onSelect(null);
    setQuery("");
  };

  return (
    <label className="block text-xs uppercase tracking-wide text-muted-foreground">
      {label}
      <div className="relative mt-2">
        <input
          type="text"
          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-gold/60 focus:outline-none"
          value={inputValue}
          placeholder={placeholder}
          onChange={(event) => {
            setQuery(event.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            blurTimeoutRef.current = window.setTimeout(() => setOpen(false), 120);
          }}
        />
        {selectedId != null && (
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full border border-border px-2 py-0.5 text-[11px] uppercase tracking-wide text-muted-foreground transition-all duration-200 hover:border-destructive/60 hover:text-destructive"
            onMouseDown={(event) => event.preventDefault()}
            onClick={handleClear}
          >
            Clear
          </button>
        )}
        {dropdownVisible && (
          <div className="absolute left-0 right-0 z-20 mt-1 max-h-60 overflow-y-auto rounded-xl border border-border bg-background/95 p-2 shadow-2xl">
            {emptyMessage ? (
              <p className="px-2 py-1 text-xs text-muted-foreground">{emptyMessage}</p>
            ) : (
              <ul className="space-y-1 text-sm text-foreground/80">
                {results.map((contact) => {
                  const labelValue = contact.company_name || contact.name || `Contact #${contact.id}`;
                  const location = [contact.location_city, contact.location_region]
                    .filter(Boolean)
                    .join(", ");
                  return (
                    <li key={contact.id}>
                      <button
                        type="button"
                        className="w-full rounded-lg border border-border px-3 py-2 text-left transition hover:border-gold/60 hover:text-gold"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => handleSelect(contact)}
                      >
                        <p className="text-sm font-semibold">{labelValue}</p>
                        {location && <p className="text-xs text-muted-foreground">{location}</p>}
                        {contact.tags?.length ? (
                          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                            {contact.tags.slice(0, 3).join(" • ")}
                          </p>
                        ) : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>
      {helperText && <p className="mt-1 text-[11px] text-muted-foreground">{helperText}</p>}
    </label>
  );
}
